/**
 * Top Customers Chart Component
 *
 * Displays a horizontal bar chart showing top customers by revenue
 * with project count indicators. Click on a bar to view customer details.
 */

import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/frontend/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import type { CustomerSummary } from "@/shared/types/sales-analytics";

interface TopCustomersChartProps {
  customers: CustomerSummary[];
  title?: string;
  description?: string;
  limit?: number;
}

const COLORS = [
  "#3b82f6", // blue-500
  "#10b981", // emerald-500
  "#f59e0b", // amber-500
  "#ef4444", // red-500
  "#8b5cf6", // violet-500
  "#ec4899", // pink-500
  "#14b8a6", // teal-500
  "#f97316", // orange-500
  "#6366f1", // indigo-500
  "#a855f7", // purple-500
];

export function TopCustomersChart({
  customers,
  title = "Top Customers",
  description = "Revenue by customer",
  limit = 10
}: TopCustomersChartProps) {
  const navigate = useNavigate();

  if (!customers || customers.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <div className="text-muted-foreground text-center py-8">No customer data available</div>
        </CardContent>
      </Card>
    );
  }

  // Limit and format data for chart
  const chartData = customers.slice(0, limit).map((customer) => ({
    name: customer.customerName.length > 25
      ? customer.customerName.substring(0, 25) + "..."
      : customer.customerName,
    fullName: customer.customerName,
    customerId: customer.customerId,
    revenue: customer.revenue,
    projectCount: customer.projectCount,
  }));

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload[0]) return null;

    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-sm mb-1">{data.fullName}</p>
        <p className="text-sm text-gray-600">
          Revenue: <span className="font-semibold text-blue-600">
            ฿{data.revenue.toLocaleString()}
          </span>
        </p>
        <p className="text-sm text-gray-600">
          Projects: <span className="font-semibold">{data.projectCount}</span>
        </p>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis
              type="number"
              tickFormatter={(value) => `฿${(value / 1000).toFixed(0)}K`}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={90}
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(59, 130, 246, 0.1)" }} />
            <Bar
              dataKey="revenue"
              radius={[0, 4, 4, 0]}
              onClick={(data: any) => {
                if (data && data.customerId) {
                  navigate(`/analytics/customers/${data.customerId}`);
                }
              }}
              style={{ cursor: "pointer" }}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        {/* Customer count summary */}
        <div className="mt-4 pt-4 border-t text-sm text-muted-foreground text-center">
          Showing top {Math.min(limit, customers.length)} of {customers.length} customers
          <span className="ml-2 text-xs text-blue-600">• Click a bar to view customer details</span>
        </div>
      </CardContent>
    </Card>
  );
}
